import React, { useEffect, useMemo, useRef } from "react";

const youtubePlayers = new Set();
let youtubeApiPromise;

const addYouTubeApiParams = (url) => {
  try {
    const parsedUrl = new URL(url);
    parsedUrl.searchParams.set("enablejsapi", "1");

    if (typeof window !== "undefined" && window.location?.origin) {
      parsedUrl.searchParams.set("origin", window.location.origin);
    }

    return parsedUrl.toString();
  } catch (error) {
    const separator = url.includes("?") ? "&" : "?";
    return `${url}${separator}enablejsapi=1`;
  }
};

const loadYouTubeApi = () => {
  if (typeof window === "undefined" || typeof document === "undefined") {
    return Promise.resolve(null);
  }

  if (window.YT?.Player) {
    return Promise.resolve(window.YT);
  }

  if (!youtubeApiPromise) {
    youtubeApiPromise = new Promise((resolve) => {
      const previousCallback = window.onYouTubeIframeAPIReady;

      window.onYouTubeIframeAPIReady = () => {
        if (typeof previousCallback === "function") {
          previousCallback();
        }

        resolve(window.YT);
      };

      if (!document.querySelector('script[src="https://www.youtube.com/iframe_api"]')) {
        const script = document.createElement("script");
        script.src = "https://www.youtube.com/iframe_api";
        document.body.appendChild(script);
      }
    });
  }

  return youtubeApiPromise;
};

const pauseOtherPlayers = (activePlayer) => {
  youtubePlayers.forEach((player) => {
    if (player !== activePlayer && typeof player.pauseVideo === "function") {
      try {
        player.pauseVideo();
      } catch (error) {
        // Player can be unavailable while carousel slides are being cloned or removed.
      }
    }
  });
};

function YouTubeVideo({ src, title, ...props }) {
  const iframeRef = useRef(null);
  const playerRef = useRef(null);
  const apiSrc = useMemo(() => addYouTubeApiParams(src), [src]);

  useEffect(() => {
    let isMounted = true;

    loadYouTubeApi().then((YT) => {
      if (!isMounted || !YT?.Player || !iframeRef.current) {
        return;
      }

      playerRef.current = new YT.Player(iframeRef.current, {
        events: {
          onStateChange: (event) => {
            if (event.data === YT.PlayerState.PLAYING) {
              pauseOtherPlayers(event.target);
            }
          },
        },
      });

      youtubePlayers.add(playerRef.current);
    });

    return () => {
      isMounted = false;

      if (playerRef.current) {
        youtubePlayers.delete(playerRef.current);
        try {
          playerRef.current.destroy();
        } catch (error) {
          // Ignore cleanup failures from YouTube's iframe API.
        }
      }
    };
  }, []);

  return (
    <iframe
      ref={iframeRef}
      src={apiSrc}
      title={title}
      {...props}
    />
  );
}

export default YouTubeVideo;
